import React, { useRef, useEffect, useMemo, useState } from "react"
import styled from "styled-components"
import gsap from "gsap"

import colors from "assets/styles/colors"
import easings from "assets/styles/easings"
import media from "assets/styles/media"
import BGGrid from "components/uIElements/BGGrid"
import text from "assets/styles/text"
import dcgLogo from "assets/images/homepage/dcgLogo.svg"
import noomLogo from "assets/images/homepage/noomLogo.svg"
import latchLogo from "assets/images/homepage/latchLogo.png"
import redoxLogo from "assets/images/homepage/redox-logo.png"
import buzzfeedLogo from "assets/images/homepage/buzzfeedLogo.svg"
import oloLogo from "assets/images/homepage/oloLogo.svg"
import kindbodyLogo from "assets/images/homepage/kindbody-logo.png"
import tiveLogo from "assets/images/homepage/tiveLogo.svg"

import { ReactComponent as bitArrowA } from "assets/images/global/bitArrowSmallA.svg"
import { ReactComponent as bitArrowB } from "assets/images/global/bitArrowSmallB.svg"
import { useMedia } from "utils/hooks"
import { LineConfig } from "components/uIElements/BGGrid"

const content = [
  {
    name: "Digital Currency Group",
    description:
      "Accelerating the development of better financial systems through blockchain technology. Backed since 2014.",
    link: "https://dcg.co/",
    image: dcgLogo,
  },
  {
    name: "Noom",
    description:
      "Helping people manage their weight and lead happier, healthier lives. Backed since 2013.",
    link: "https://www.noom.com/",
    image: noomLogo,
  },

  {
    name: "Latch",
    description:
      "Digitizing access to the spaces that matter. Backed since 2017.",
    link: "https://www.latch.com/",
    image: latchLogo,
  },
  {
    name: "Redox",
    description:
      "Unlocking healthcare data interoperability. Backed since 2017.",
    link: "https://www.redoxengine.com/",
    image: redoxLogo,
  },
  {
    name: "Buzzfeed",
    description: "Pioneering modern media site. Backed since 2010.",
    link: "https://www.buzzfeed.com/",
    image: buzzfeedLogo,
  },
  {
    name: "Olo",
    description:
      "Digitizing online ordering for the food industry. Backed since 2008.",
    link: "https://www.olo.com/",
    image: oloLogo,
  },
  {
    name: "Kindbody",
    description: "Fertility and family building for all. Backed since 2016.",
    link: "https://kindbody.com/",
    image: kindbodyLogo,
  },
  {
    name: "Tive",
    description:
      "Bringing modern tracking and sensors to build a connected supply chain. Backed since 2019.",
    link: "https://www.tive.com/",
    image: tiveLogo,
  },
]

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

const Hero: React.FC = () => {
  const [wrapper, setWrapper] = useState<HTMLElement | null>(null)
  const left = useRef<HTMLDivElement>(null)
  const right = useRef<HTMLDivElement>(null)
  const middle = useRef<HTMLDivElement>(null)
  const leftVal = useMedia("-30.472vw", "-10.472vw", "-10.472vw", "-50vh")
  const centerVal = useMedia("-12.917vw", "-12.917vw", "-12.917vw", "-80vh")
  const rightVal = useMedia("-10.472vw", "-10.472vw", "-10.472vw", "-50vh")
  useEffect(() => {
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: wrapper,
        scrub: true,
        scroller: "#___gatsby",
        start: "top bottom",
        end: "bottom top",
      },
    })

    //increase left bar size and move it up a bit
    if (left.current) {
      tl.to(left.current, {
        scaleY: 1.5,
        y: leftVal,
      }, 0)
    }

    //keep center bar on screen
    if (middle.current) {
      tl.to(middle.current, {
        y: centerVal,
      }, 0)
    }

    //make right bar a bit taller
    if (right.current) {
      tl.to(right.current, {
        scaleY: 3,
        y: rightVal,
      }, 0)
    }

    return () => {
      tl.kill()
    }
  }, [wrapper])

  const lineConfig: LineConfig = {
    sectionHeight: "100%",
    animationClass: "relationships",
    sectionRef: wrapper,
    filledColumns: [
      {
        column: useMedia(0, 0, 0, 0),
        height: useMedia("29.72vw", "29.72vw", "23.44vw", "29.72vw"),
        topOffset: useMedia("80%", "80%", "161.82vw", "80%"),
        color: colors.lightBlueGradient,
        ref: left,
        zIndex: "-1",
      },
      {
        column: useMedia(3, 3, 1, 1),
        height: useMedia("5.56vw", "5.56vw", "14.65vw", "14.65vw"),
        topOffset: useMedia("15%", "15%", "19.53vw", "15%"),
        color: colors.lightBlueGradient,
        ref: middle,
        zIndex: "-1",
      },
      {
        column: useMedia(9, 9, 4, 4),
        height: useMedia("13.89vw", "13.89vw", "23.44vw", "13.89vw"),
        topOffset: useMedia("50%", "50%", "81.84vw", "50%"),
        color: colors.lightBlueGradient,
        ref: right,
        zIndex: "-1",
      },
    ],
  }

  const animateOut = async (href: string, index: number) => {
    gsap.to(`#arrow-${index}`, {
      duration: 0.5,
      x: "50%",
      rotate: 45,
      opacity: 0,
      onComplete: () => {
        gsap.set(`#arrow-${index}`, {
          x: "0%",
          rotate: 0,
          opacity: 1,
        })
      },
    })
    await sleep(500)
    window.open(href, "_blank")
  }

  const relationships = useMemo(
    () =>
      content.map((item, i) => {
        return (
          <Relationship
            onClick={e => animateOut(item.link, i)}
            key={`relationship__${i}`}
          >
            <BackgroundLogo
              className={`relationship-image${i}`}
              alt={item.name + " logo"}
              src={item.image}
            ></BackgroundLogo>
            <MobileWrapper>
              <Name>{item.name}</Name>
              <Description>{item.description}</Description>
              <Arrow id={`arrow-${i}`} />
            </MobileWrapper>
            <Visit>
              Visit Site <LinkArrow />
            </Visit>
          </Relationship>
        )
      }),
    [content]
  )

  return (
    <Wrapper
      id="Relationships"
      data-scroll
      data-scroll-section
      ref={ref => setWrapper(ref)}
    >
      <BGGrid lineConfig={lineConfig} />
      <Header>
        Some of our proudest <span>relationships.</span>
      </Header>
      <Relationships>{relationships}</Relationships>
    </Wrapper>
  )
}

export default Hero

const Wrapper = styled.section`
  background: ${colors.white};

  width: 100%;
  padding: 17.361vw 12.9vw;
  ${media.tablet} {
    height: 189.94vw;
    padding: 14.65vw 4.88vw;
  }
  ${media.mobile} {
    padding: 23.47vw 4vw;
  }
`

const Header = styled.h2`
  font-family: Neue Montreal;
  font-style: normal;
  font-weight: normal;
  font-size: 8.333vw;
  line-height: 100%;
  color: ${colors.blue400};
  position: relative;
  z-index: 2;

  span {
    font-family: "NeueBit";
    font-style: normal;
    font-weight: bold;
    font-size: 11.806vw;
    line-height: 80%;
  }
  ${media.tablet} {
    ${text.tablet.h3};
    span {
      ${text.tablet.d2Point5};
    }
  }

  ${media.mobile} {
    ${text.mobile.h4};

    span {
      font-size: 24vw;
    }
  }
`

const Relationships = styled.div`
  margin-top: 5.208vw;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-auto-rows: 20.833vw;
  grid-gap: 1.389vw 1.736vw;
  position: relative;
  z-index: 2;
  width: 73.2vw;
  ${media.tablet} {
    grid-auto-rows: 28.13vw;
    grid-template-columns: 44.14vw 44.14vw;

    grid-gap: 2.34vw 1.95vw;
    margin-top: 4.88vw;
  }

  ${media.mobile} {
    grid-template-columns: 1fr;
    grid-auto-rows: 60.27vw;
    grid-gap: 0 5.33vw 5.33vw;
    margin-top: 9.6vw;
  }
`

const MobileWrapper = styled.div`
  display: grid;
  grid-template-columns: 8.333vw auto 2.083vw;
  grid-template-rows: auto;
  grid-gap: 2.083vw 1.389vw;

  ${media.tablet} {
    grid-template-columns: 11.72vw auto 1.95vw;
    grid-gap: 1.95vw 1.95vw;
  }
  ${media.mobile} {
    grid-template-columns: 21.33vw auto 5.33vw;
    grid-template-rows: auto;

    grid-gap: 0 2.67vw;
    height: fit-content;
    margin-bottom: 6.4vw;
  }
`

const Name = styled.h3`
  font-family: Neue Montreal;
  font-style: normal;
  font-weight: normal;
  font-size: 2.083vw;
  line-height: 120%;
  grid-row: span 2;
  position: relative;
  z-index: 1;
  ${media.tablet} {
    ${text.tablet.h5};
  }
  ${media.mobile} {
    font-size: 5.33vw;
    height: fit-content;
  }
`

const Description = styled.p`
  font-family: Neue Montreal;
  font-style: normal;
  font-weight: normal;
  font-size: 1.25vw;
  line-height: 140%;
  letter-spacing: 0.01em;
  position: relative;
  z-index: 1;
  ${media.tablet} {
    ${text.tablet.bodyL};
    width: 25.98vw;
  }
  ${media.mobile} {
    ${text.mobile.bodyS};
    width: 58.67vw;
    height: fit-content;
  }
`

const Arrow = styled(bitArrowB)`
  margin-right: 0.694vw;
  transition: transform 0.4s ${easings.main}, opacity 0.4s ${easings.main};
  transform-origin: 0.694vw 0.694vw;
  width: 1.39vw;
  height: 1.39vw;
  position: relative;
  z-index: 1;
  ${media.tablet} {
    width: 2.5vw;
    height: 2.5vw;
    transform-origin: 1.25vw 1.25vw;
    margin-right: 1.25vw;
  }
  ${media.mobile} {
    margin-right: 1.33vw;
    transform-origin: 1.33vw 1.33vw;
    height: 5.33vw;
    width: 5.33vw;
  }
`

const Visit = styled.p`
  font-family: NeueBit;
  font-style: normal;
  font-weight: bold;
  font-size: 1.25vw;
  line-height: 100%;
  text-transform: uppercase;
  margin-left: 9.9vw;
  color: ${colors.blue400};
  position: relative;
  z-index: 1;
  ${media.tablet} {
    ${text.tablet.buttonText};
    margin-left: 14vw;
  }
  ${media.mobile} {
    ${text.mobile.buttonText};
    margin-left: 24vw;
  }
`

const LinkArrow = styled(bitArrowA)`
  height: 1.042vw;
  margin-left: 10px;
  transition: transform 0.4s ${easings.main};
  ${media.tablet} {
    width: 1.46vw;
    height: 1.46vw;
    margin-left: 0.98vw;
  }
  ${media.mobile} {
    width: 3.73vw;
    height: 3.73vw;
    margin-left: 2.67vw;
  }
`

const BackgroundLogo = styled.img`
  position: absolute;
  top: 4.17vw;
  left: 2.08vw;
  z-index: -1;
  width: auto;
  height: auto;
  max-width: 32.85vw;
  max-height: 13.89vw;

  ${media.tablet} {
    left: 3.91vw;
    top: 9.77vw;
    max-width: initial;
    max-height: initial;
    bottom: auto;
  }
  ${media.mobile} {
    max-width: initial;
    max-height: initial;
    bottom: auto;

    left: 10.67vw;
    top: 26.67vw;
  }
`

const Relationship = styled.div`
  color: ${colors.black};
  cursor: pointer;
  border-top: 1px solid black;
  padding-top: 1.667vw;
  position: relative;
  :before {
    content: "";
    position: absolute;
    width: 0%;
    height: 1px;
    background-color: black;
    top: 0;
    left: 0;
    transition: width 0.4s ${easings.main};
  }

  ${media.hover} {
    :hover {
      :before {
        width: 100%;
      }

      ${Arrow} {
        transform: rotate(45deg);
      }

      ${LinkArrow} {
        transform: translateX(0.694vw);
      }
    }
  }

  .relationship-image0 {
    max-width: initial;
    max-height: initial;
    width: 20.833vw;
    height: 13.749vw;
    top: 5.134vw;
    object-fit: contain;
    object-position: top;

    bottom: auto;
  }
  .relationship-image2 {
    max-width: initial;
    max-height: initial;
    width: 28.4vw;
    height: 6.25vw;

    bottom: auto;
  }
  .relationship-image3 {
    max-width: initial;
    max-height: initial;
    width: 31.18vw;
    height: 5.97vw;

    bottom: auto;
  }

  .relationship-image4 {
    max-width: initial;
    max-height: initial;
    width: 27.78vw;
    height: 4.93vw;

    bottom: auto;
  }
  .relationship-image5 {
    max-width: initial;
    max-height: initial;
    width: 17.36vw;
    height: 10.63vw;

    bottom: auto;
  }
  .relationship-image6 {
    max-width: initial;
    max-height: initial;
    width: 32.847vw;
    height: auto;

    bottom: auto;
  }
  .relationship-image7 {
    max-width: initial;
    max-height: initial;
    width: 24.24vw;
    height: 7.57vw;

    bottom: auto;
  }
  ${media.tablet} {
    padding-top: 2.34vw;
    .relationship-image0 {
      width: 29.3vw;
      height: 19.34vw;
      top: 4vw;
    }
    .relationship-image1 {
      width: 27.93vw;
      height: 18.46vw;
    }
    .relationship-image2 {
      width: 28.03vw;
      height: 6.25vw;
    }
    .relationship-image3 {
      width: 28.91vw;
      height: 5.27vw;
    }

    .relationship-image4 {
      width: 29.79vw;
      height: 5.27vw;
    }
    .relationship-image5 {
      width: 16.6vw;
      height: 10.16vw;
    }
    .relationship-image6 {
      width: 40.23vw;
    }
    .relationship-image7 {
      width: 37.3vw;
      height: 7.42vw;
    }
  }

  ${media.mobile} {
    padding-top: 8.53vw;
    display: block;
    .relationship-image0 {
      width: 80vw;
      height: 17.87vw;
    }
    .relationship-image1 {
      width: 51.2vw;
      height: 33.87vw;
    }
    .relationship-image2 {
      width: 76.53vw;
      height: 17.07vw;
    }

    .relationship-image3 {
      width: 78.93vw;
      height: 14.4vw;
    }
    .relationship-image4 {
      width: 81.33vw;
      height: 14.4vw;
    }

    .relationship-image5 {
      width: 45.33vw;
      height: 27.73vw;
    }
    .relationship-image6 {
      width: 90.67vw;
      left: 3.13vw;
      top: 24.53vw;
    }
    .relationship-image7 {
      width: 70.93vw;
      height: 21.33vw;
    }
  }
`
